import Checkbox from '@components/v2/form/Checkbox/Checkbox';
import Select from '@components/v2/form/Select/Select';
import MenuItem from '@components/v2/ui/Menu/MenuItem';
import Typography from '@components/v2/ui/Typography/Typography';
import { Stack } from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import LangEnum from '../../../lang/LangEnum';
import { ShareCatalogTestIds } from '../../../utils/testIds/ShareCatalog.testIds';
import { useTranslation } from '../../translation/TranslationContext';
import { AccessConfig, ShareChannelEnum } from '../types/sharedEntities.enum';
import { IShareCatalogContent, ISharedEntityForm } from '../types/sharedEntities.types';
import ChannelButton from './ChannelButton';
import EmailAutocomplete from './EmailAutocomplete';
import PriceTableAutocomplete from './PriceTableAutocomplete';

const PUBLIC_ACCESS_FIELDS = [
  {
    name: 'config.enableProductDetails',
    label: LangEnum.PERSONALIZE_PUBLIC_ACCESS_PRODUCT_DETAILS,
  },
  { name: 'config.showAvailableStock', label: LangEnum.PERSONALIZE_PUBLIC_ACCESS_STOCK },
  { name: 'config.showSellInPrices', label: LangEnum.PERSONALIZE_PUBLIC_ACCESS_SELL_IN },
  { name: 'config.showSellOutPrices', label: LangEnum.PERSONALIZE_PUBLIC_ACCESS_SELL_OUT },
] as const;

const ShareCatalogContent = ({ entityId, disabledConfigAccess }: IShareCatalogContent) => {
  const { translate } = useTranslation();
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext<ISharedEntityForm>();

  const channelType = useWatch({ control, name: 'config.channelType' });
  const accessConfig = useWatch({ control, name: 'accessConfig' });
  const isDefaultPriceTableId = useWatch({ control, name: 'config.isDefaultPriceTableId' });
  const isShowSellInPrices = useWatch({ control, name: 'config.showSellInPrices' });
  const isShowSellOutPrices = useWatch({ control, name: 'config.showSellOutPrices' });
  const isShowPrices = isShowSellInPrices || isShowSellOutPrices;
  const isPublic = accessConfig === AccessConfig.Public;
  const isShowPriceTableField = isShowPrices && !isDefaultPriceTableId;

  const isEmail = channelType === ShareChannelEnum.EMAIL;
  const isWhatsapp = channelType === ShareChannelEnum.WHATSAPP;

  return (
    <Stack spacing={2.5} useFlexGap>
      <Stack spacing={1} useFlexGap>
        <Controller
          control={control}
          name="accessConfig"
          render={({ field }) => (
            <Select
              name={field.name}
              value={field.value}
              disabled={disabledConfigAccess}
              label={translate(LangEnum.GLOBAL_CONFIG_ACCESS)}
              testId={ShareCatalogTestIds.ACCESS_CONFIG_SELECT}
              onChange={(event) => field.onChange(event.target.value)}
            >
              <MenuItem
                value={AccessConfig.Private}
                testId={ShareCatalogTestIds.ACCESS_CONFIG_OPTION_PRIVATE}
              >
                {translate(LangEnum.SHARE_ORDER_PRIVATE_ACCESS)}
              </MenuItem>
              <MenuItem
                value={AccessConfig.Public}
                testId={ShareCatalogTestIds.ACCESS_CONFIG_OPTION_PUBLIC}
              >
                {translate(LangEnum.SHARE_ORDER_PUBLIC_ACCESS)}
              </MenuItem>
            </Select>
          )}
        />
        <Typography variant="body2" color="textSecondary">
          {translate(
            isPublic ? LangEnum.PUBLIC_SHARE_CATALOG_INFO : LangEnum.PRIVATE_SHARE_CATALOG_INFO,
          )}
        </Typography>
      </Stack>

      {isPublic && (
        <Stack spacing={0.5} useFlexGap>
          <Typography variant="body2" color="textSecondary">
            {translate(LangEnum.PERSONALIZE_PUBLIC_ACCESS_TITLE)}
          </Typography>
          {PUBLIC_ACCESS_FIELDS.map((publicField) => (
            <Controller
              key={publicField.name}
              name={publicField.name}
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <Checkbox
                  size="small"
                  checked={field.value}
                  label={translate(publicField.label)}
                  onChange={(_, isChecked) => field.onChange(isChecked)}
                />
              )}
            />
          ))}
          {isShowPrices && (
            <Controller
              name="config.isDefaultPriceTableId"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <Checkbox
                  size="small"
                  checked={field.value}
                  label={translate(LangEnum.PERSONALIZE_PUBLIC_ACCESS_PRICE_TABLE)}
                  onChange={(_, isChecked) => {
                    field.onChange(isChecked);
                    setValue('config.priceTableId', undefined);
                  }}
                />
              )}
            />
          )}
        </Stack>
      )}

      {isShowPriceTableField && (
        <Controller
          control={control}
          name="config.priceTableId"
          render={({ field }) => <PriceTableAutocomplete field={field} entityId={entityId} />}
        />
      )}
      {isShowPriceTableField && errors?.config?.priceTableId && (
        <Typography variant="body2" color="error">
          {errors.config.priceTableId.message}
        </Typography>
      )}

      <Stack spacing={1} useFlexGap>
        <Typography variant="body2" color="textSecondary">
          {translate(LangEnum.GLOBAL_SHARE_BY)}
        </Typography>
        <Stack direction="row" spacing={1} useFlexGap>
          <ChannelButton
            label={LangEnum.GLOBAL_WHATSAPP}
            isActive={isWhatsapp}
            handleClick={() => setValue('config.channelType', ShareChannelEnum.WHATSAPP)}
          />
          <ChannelButton
            label={LangEnum.GLOBAL_EMAIL}
            isActive={isEmail}
            handleClick={() => setValue('config.channelType', ShareChannelEnum.EMAIL)}
          />
        </Stack>
      </Stack>

      {isEmail && (
        <Stack spacing={0.5} useFlexGap>
          <Controller
            control={control}
            name="config.emails"
            render={({ field }) => <EmailAutocomplete field={field} entityId={entityId} />}
          />
          {errors?.config?.emails && (
            <Typography variant="body2" color="error">
              {errors.config.emails.message}
            </Typography>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default ShareCatalogContent;
